import * as React from "react";
import { debounce, entries } from "lodash";
const { Component } = React;
import Color from "colorjs.io";

import Files from "react-files";

import * as UserStore from "../users/UserStore";
import { Modal, Button, Input, FormGroup, Label } from "../components/Modal";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import * as OrganizationStore from "./OrganizationStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface OrganizationBrandingState {
    organizationId: string;
    isLoading: boolean;
    brandingContent: any;
    schema: any;
    themeKeys: any;
    localizations: any;
    showCreate: boolean;
    imageHashes: number;
    waitingForSave: boolean;
    domains: string[];
    newDomain: string;
}

interface ContrastInfo {
    lightModeContrast: number;
    darkModeContrast: number;
    lightModeWarning: boolean;
    darkModeWarning: boolean;
    suggestedLightMode?: string;
    suggestedDarkMode?: string;
}

interface OrganizationBrandingProps {
    organization: string;
}

let contextSubscriptionId = "";

let objectMap = (obj, func) => {
    return entries(obj).map((kv) => {
        let [k, v] = kv;
        return func(k, v);
    });
};

// WCAG AA requires 4.5:1 for normal text, 3:1 for large text
const MIN_CONTRAST_RATIO = 4.5;

function checkColorContrast(colorValue: string): ContrastInfo {
    try {
        const color = new Color(colorValue);
        const white = new Color("white");
        const black = new Color("black");
        
        // Calculate contrast ratios
        const lightModeContrast = Math.abs(white.contrast(color, "WCAG21"));
        const darkModeContrast = Math.abs(black.contrast(color, "WCAG21"));
        
        const lightModeWarning = lightModeContrast < MIN_CONTRAST_RATIO;
        const darkModeWarning = darkModeContrast < MIN_CONTRAST_RATIO;
        
        let suggestedLightMode: string | undefined;
        let suggestedDarkMode: string | undefined;
        
        // Suggest better colors if contrast is insufficient
        if (lightModeWarning) {
            suggestedLightMode = suggestBetterColor(color, white, MIN_CONTRAST_RATIO);
        }
        
        if (darkModeWarning) {
            suggestedDarkMode = suggestBetterColor(color, black, MIN_CONTRAST_RATIO);
        }
        
        return {
            lightModeContrast,
            darkModeContrast,
            lightModeWarning,
            darkModeWarning,
            suggestedLightMode,
            suggestedDarkMode,
        };
    } catch (e) {
        // Invalid color, return neutral values
        return {
            lightModeContrast: 0,
            darkModeContrast: 0,
            lightModeWarning: false,
            darkModeWarning: false,
        };
    }
}

function suggestBetterColor(baseColor: any, background: any, targetContrast: number): string {
    try {
        // Get the color in LCH color space (better for manipulation)
        const lch = baseColor.to("lch");
        const bgLch = background.to("lch");
        
        // Determine if we need to go lighter or darker
        const shouldDarken = bgLch.l > 50;
        
        // Adjust lightness to achieve target contrast
        let adjustedColor = lch.clone();
        let step = shouldDarken ? -5 : 5;
        let iterations = 0;
        const maxIterations = 20;
        
        while (iterations < maxIterations) {
            adjustedColor.l = Math.max(0, Math.min(100, adjustedColor.l + step));
            const contrast = Math.abs(background.contrast(adjustedColor, "WCAG21"));
            
            if (contrast >= targetContrast) {
                return adjustedColor.to("srgb").toString({ format: "hex" });
            }
            
            iterations++;
        }
        
        // Fallback: return white or black depending on background
        return shouldDarken ? "#000000" : "#ffffff";
    } catch (e) {
        return "#000000";
    }
}

export default class OrganizationBranding extends Component<OrganizationBrandingProps, OrganizationBrandingState> {
    constructor(props) {
        super(props);
        this.state = {
            organizationId: "",
            brandingContent: {},
            isLoading: true,
            schema: {},
            themeKeys: [],
            localizations: [],
            showCreate: false,
            imageHashes: +new Date(),
            waitingForSave: false,
            domains: [],
            newDomain: "",
        };
    }

    componentDidMount() {
        contextSubscriptionId = GetContext((context) => {
            this.GetBranding(context.currentOrganization.id);
        });

        this.debounceUpdate = debounce(function () {
            OrganizationStore.UpdateBranding(this.state.organizationId, this.state.brandingContent).then(() => {
                this.setState({ imageHashes: +new Date(), waitingForSave: false });
            });
        }, 750);
    }

    GetBranding = (organizationId) => {
        this.setState({
            organizationId: "",
            brandingContent: {},
            isLoading: true,
            schema: {},
            themeKeys: [],
            localizations: [],
            showCreate: false,
            domains: [],
        });

        OrganizationStore.GetBranding(organizationId)
            .then((res) => {
                return this.setState({
                    organizationId,
                    brandingContent: res.data,
                    themeKeys: objectMap(res.data.theme, (key, value) => ({ key, value })),
                    localizations: objectMap(res.data.localization, (language, values) => ({ language, values: objectMap(values, (key, value) => ({ key, value })) })),
                    isLoading: false,
                });
            })
            .catch(() => {
                return this.setState({
                    organizationId,
                    isLoading: false,
                    showCreate: true,
                });
            });

        OrganizationStore.GetBrandingDomains(organizationId).then((res) => {
            this.setState({ domains: res.data });
        });
    };

    componentWillUnmount() {
        UnsubscribeContext(contextSubscriptionId);
    }

    handleChange = (value) => {
        this.setState({ brandingContent: value }, this.debounceUpdate);
    };

    debounceUpdate = () => {};

    addNewImage = () => {
        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images.push({ key: "", isRedirectURI: true, redirectURI: "" });

            return { brandingContent };
        }, this.save);
    };

    handleImageKeyChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].key = value;

            return { brandingContent };
        }, this.save);
    };

    handleImageIsRedirectChange = (i) => (e) => {
        let checked = e.target.checked;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].isRedirect = checked;

            return { brandingContent };
        }, this.save);
    };

    handleImageIsNotRedirectChange = (i) => (e) => {
        let checked = !e.target.checked;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].isRedirect = checked;

            return { brandingContent };
        }, this.save);
    };

    handleImageRedirectURIChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].redirectURI = value;

            return { brandingContent };
        }, this.save);
    };

    handleImageMimeChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].mimeType = value;

            return { brandingContent };
        }, this.save);
    };

    handleImageFilesChange = (i) => (files) => {
        var reader = new FileReader();

        reader.onload = (evt: any) => {
            this.setState((prevState) => {
                let { brandingContent } = prevState;

                let dataRegex = /data:((\w|-)+(\/((\w|-)+))+);base64,(.+)/;

                let result = dataRegex.exec(evt.target.result.toString());
                if (result != null) {
                    brandingContent.images[i].content = result[6];
                    brandingContent.images[i].mimeType = result[1];
                }

                return { brandingContent };
            }, this.save);
        };

        reader.readAsDataURL(files[0]);
    };

    removeImage = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images.splice(i, 1);

            return { brandingContent };
        }, this.save);
    };

    handleThemeKeyChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys[i].key = value;

            return { themeKeys };
        }, this.save);
    };

    handleThemeValueChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys[i].value = value;

            return { themeKeys };
        }, this.save);
    };

    removeThemeKey = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys.splice(i, 1);

            return { themeKeys };
        }, this.save);
    };

    handleLocalizationKeyChange = (language, i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[language].values[i].key = value;

            return { localizations };
        }, this.save);
    };

    handleLocalizationValueChange = (language, i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[language].values[i].value = value;

            return { localizations };
        }, this.save);
    };

    removeLocalizationProperty = (language, i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[language].values.splice(i, 1);

            return { localizations };
        }, this.save);
    };

    addNewThemeKey = () => {
        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys.push({ key: "", value: "" });

            return { themeKeys };
        }, this.save);
    };

    addNewLocalizationProperty = (l) => () => {
        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[l].values.push({ key: "", value: "" });

            return { localizations };
        }, this.save);
    };

    createBranding = () => {
        OrganizationStore.CreateBranding(this.state.organizationId).then(() => this.GetBranding(this.state.organizationId));
    };

    save = () => {
        this.setState(
            (prevState) => {
                let { brandingContent, themeKeys, localizations } = prevState;

                brandingContent.theme = themeKeys.reduce((agg, cur) => {
                    agg[cur.key] = cur.value;
                    return agg;
                }, {});

                brandingContent.localization = localizations.reduce((a, c) => {
                    a[c.language] = c.values.reduce((agg, cur) => {
                        agg[cur.key] = cur.value;
                        return agg;
                    }, {});
                    return a;
                }, {});

                return { brandingContent, waitingForSave: true };
            },
            () => {
                this.debounceUpdate();
            }
        );
    };

    copyLink = (link) => () => {
        var url = window.location.origin + link;
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard.writeText(url).then(
                    function () {
                        /* clipboard successfully set */
                    },
                    function (e) {
                        console.log(e);
                        /* clipboard write failed */
                    }
                );
            }
        });
    };

    removeDomain = (domain) => () => {
        OrganizationStore.RemoveDomain(this.state.organizationId, domain).then(() => this.GetBranding(this.state.organizationId));
    };

    handleNewDomainChange = (e) => {
        let value = e.target.value;
        this.setState({ newDomain: value });
    };

    addDomain = () => {
        OrganizationStore.AddDomain(this.state.organizationId, this.state.newDomain).then(() => this.GetBranding(this.state.organizationId));
    };

    addLocaliztion = (language) => () => {
        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations.push({ language: language, values: [] });

            return { localizations };
        }, this.save);
    };

    render() {
        return (
            !this.state.isLoading && (
                <>
                    {/* Header Section */}
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                        <FontAwesomeIcon icon={["fad", "palette"]} className="text-3xl text-blue-500" />
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800">Branding</h2>
                            <p className="text-sm text-gray-600">Manage your organization's branding settings</p>
                        </div>
                    </div>
                    {this.state.showCreate && (
                        <div className="flex justify-center items-center py-12">
                            <Button variant="primary" onClick={this.createBranding}>
                                <FontAwesomeIcon icon={["fad", "plus-circle"]} className="mr-2" />
                                Create Branding
                            </Button>
                        </div>
                    )}
                    {!this.state.showCreate && (
                        <div className="space-y-8">
                            {/* Domains Section */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <FontAwesomeIcon icon={["fad", "globe"]} className="mr-2 text-blue-600" />
                                    Domains
                                </h3>
                                <div className="space-y-3">
                                    {this.state.domains.length === 0 ? (
                                        <p className="text-sm text-gray-500 italic">No domains configured</p>
                                    ) : (
                                        this.state.domains.map((domain: string, key: number) => (
                                            <div key={"domain" + key} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                                                <span className="text-sm font-medium text-gray-900 font-mono">{domain}</span>
                                                <Button variant="outline-danger" size="sm" onClick={this.removeDomain(domain)}>
                                                    <FontAwesomeIcon icon={["fad", "trash"]} />
                                                </Button>
                                            </div>
                                        ))
                                    )}
                                    <div className="flex items-center space-x-3 pt-3">
                                        <div className="flex-1">
                                            <Input type="text" placeholder="example.com" value={this.state.newDomain} onChange={this.handleNewDomainChange} />
                                        </div>
                                        <Button variant="success" onClick={this.addDomain}>
                                            <FontAwesomeIcon icon={["fad", "plus"]} className="mr-2" />
                                            Add Domain
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            {/* Images Section */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                                    <FontAwesomeIcon icon={["fad", "images"]} className="mr-2 text-blue-600" />
                                    Images
                                    {this.state.waitingForSave && <FontAwesomeIcon icon={["fad", "spinner"]} spin={true} className="ml-2 text-blue-500"></FontAwesomeIcon>}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {this.state.brandingContent.images.map((image: any, i: number) => (
                                        <div key={`i${i}`} className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:shadow-md transition-shadow">
                                            {/* Image Preview */}
                                            <div className="h-48 flex items-center justify-center bg-white rounded-md mb-4 border border-gray-200">
                                                <img
                                                    key={`i${i}hash${this.state.imageHashes}}`}
                                                    src={`/static/branding/images/${image.key}?brandingId=${this.state.organizationId}&hash=${this.state.imageHashes}`}
                                                    alt={image.key || "Branding image"}
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            </div>

                                            {/* Image Key */}
                                            <FormGroup>
                                                <Label htmlFor={`i${i}key`}>Image Key</Label>
                                                <Input id={`i${i}key`} type="text" placeholder="logo, icon, banner..." value={image.key} onChange={this.handleImageKeyChange(i)}></Input>
                                            </FormGroup>

                                            {/* Image Type Radio */}
                                            <div className="flex items-center space-x-4 mb-4">
                                                <Label className="flex items-center space-x-2 cursor-pointer">
                                                    <Input type="radio" name={`imageType${i}`} id={`i${i}Image`} checked={!image.isRedirect} onChange={this.handleImageIsNotRedirectChange(i)} />
                                                    <span className="text-sm font-medium text-gray-700">File Upload</span>
                                                </Label>
                                                <Label className="flex items-center space-x-2 cursor-pointer">
                                                    <Input type="radio" name={`imageType${i}`} id={`i${i}redirect`} checked={image.isRedirect} onChange={this.handleImageIsRedirectChange(i)} />
                                                    <span className="text-sm font-medium text-gray-700">External URL</span>
                                                </Label>
                                            </div>

                                            {/* URL Input */}
                                            {image.isRedirect && (
                                                <FormGroup>
                                                    <Label htmlFor={`i${i}url`}>Image URL</Label>
                                                    <Input
                                                        id={`i${i}url`}
                                                        type="text"
                                                        placeholder="https://example.com/image.png"
                                                        value={image.redirectURI}
                                                        onChange={this.handleImageRedirectURIChange(i)}
                                                    ></Input>
                                                </FormGroup>
                                            )}

                                            {/* File Upload */}
                                            {!image.isRedirect && (
                                                <>
                                                    <Files
                                                        className="files-dropzone mb-3"
                                                        onChange={this.handleImageFilesChange(i)}
                                                        accepts={[".jpeg", ".jpg", ".png"]}
                                                        maxFileSize={10000000}
                                                        minFileSize={0}
                                                        clickable
                                                    >
                                                        <div
                                                            className={`p-4 border-2 border-dashed rounded-md text-center cursor-pointer transition-colors ${
                                                                image.content ? "border-blue-400 bg-blue-50 text-blue-700" : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
                                                            }`}
                                                        >
                                                            <FontAwesomeIcon icon={["fad", "cloud-upload"]} className="text-2xl mb-2" />
                                                            <p className="text-sm font-medium">{image.content ? "Drop or click to update" : "Drop file here or click to upload"}</p>
                                                            <p className="text-xs mt-1">JPG, JPEG, PNG (Max 10MB)</p>
                                                        </div>
                                                    </Files>
                                                    <FormGroup>
                                                        <Label htmlFor={`i${i}mime`}>MIME Type</Label>
                                                        <Input id={`i${i}mime`} type="text" placeholder="image/png" value={image.mimeType} onChange={this.handleImageMimeChange(i)}></Input>
                                                    </FormGroup>
                                                </>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                                <Button variant="outline-secondary" size="sm" onClick={this.copyLink(`/static/branding/images/${image.key}?brandingId=${this.state.organizationId}`)}>
                                                    <FontAwesomeIcon icon={["fad", "link"]} className="mr-1" />
                                                    Copy Link
                                                </Button>
                                                <Button variant="outline-danger" size="sm" onClick={this.removeImage(i)}>
                                                    <FontAwesomeIcon icon={["fad", "trash"]} />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6">
                                    <Button variant="outline-primary" onClick={this.addNewImage}>
                                        <FontAwesomeIcon icon={["fad", "plus-circle"]} className="mr-2" />
                                        Add New Image
                                    </Button>
                                </div>
                            </div>
                            {/* Theme Section */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                                    <FontAwesomeIcon icon={["fad", "palette"]} className="mr-2 text-blue-600" />
                                    Theme Colors
                                    {this.state.waitingForSave && <FontAwesomeIcon icon={["fad", "spinner"]} spin={true} className="ml-2 text-blue-500"></FontAwesomeIcon>}
                                </h3>
                                <div className="space-y-4">
                                    {this.state.themeKeys.map((themeKey: any, t: number) => {
                                        const contrastInfo = checkColorContrast(themeKey.value);
                                        const hasWarning = contrastInfo.lightModeWarning || contrastInfo.darkModeWarning;
                                        
                                        return (
                                            <div key={`t${t}`} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="grid grid-cols-12 gap-4 items-start">
                                                    <div className="col-span-12 sm:col-span-4">
                                                        <Label>Property Name</Label>
                                                        <Input type="text" placeholder="accent, primary-color..." value={themeKey.key} onChange={this.handleThemeKeyChange(t)}></Input>
                                                    </div>
                                                    <div className="col-span-12 sm:col-span-6">
                                                        <Label>Color Value</Label>
                                                        <div className="flex items-center space-x-2">
                                                            <Input type="text" placeholder="#000000" value={themeKey.value} onChange={this.handleThemeValueChange(t)} className="flex-1"></Input>
                                                            <div className="relative">
                                                                <Input type="color" value={themeKey.value} onChange={this.handleThemeValueChange(t)} className="h-10 w-16 cursor-pointer" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-span-12 sm:col-span-2 flex items-end">
                                                        <Button variant="outline-danger" onClick={this.removeThemeKey(t)} className="w-full sm:w-auto">
                                                            <FontAwesomeIcon icon={["fad", "trash"]} />
                                                        </Button>
                                                    </div>
                                                </div>
                                                
                                                {/* Contrast Warnings */}
                                                {hasWarning && (
                                                    <div className="mt-3 space-y-2">
                                                        {contrastInfo.lightModeWarning && (
                                                            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                                                <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-yellow-600 mt-0.5" />
                                                                <div className="flex-1 text-sm">
                                                                    <p className="font-semibold text-yellow-800">Low contrast on light backgrounds</p>
                                                                    <p className="text-yellow-700">Contrast ratio: {contrastInfo.lightModeContrast.toFixed(2)}:1 (minimum: {MIN_CONTRAST_RATIO}:1)</p>
                                                                    {contrastInfo.suggestedLightMode && (
                                                                        <div className="mt-2 flex items-center gap-2">
                                                                            <span className="text-yellow-700">Suggested:</span>
                                                                            <code className="px-2 py-1 bg-white rounded border border-yellow-300 text-yellow-900 font-mono text-xs">
                                                                                {contrastInfo.suggestedLightMode}
                                                                            </code>
                                                                            <div 
                                                                                className="w-6 h-6 rounded border border-yellow-300" 
                                                                                style={{ backgroundColor: contrastInfo.suggestedLightMode }}
                                                                                title={contrastInfo.suggestedLightMode}
                                                                            />
                                                                            <button
                                                                                onClick={() => {
                                                                                    const event = { target: { value: contrastInfo.suggestedLightMode } };
                                                                                    this.handleThemeValueChange(t)(event);
                                                                                }}
                                                                                className="text-xs px-2 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                                                                            >
                                                                                Apply
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {contrastInfo.darkModeWarning && (
                                                            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                                <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-blue-600 mt-0.5" />
                                                                <div className="flex-1 text-sm">
                                                                    <p className="font-semibold text-blue-800">Low contrast on dark backgrounds</p>
                                                                    <p className="text-blue-700">Contrast ratio: {contrastInfo.darkModeContrast.toFixed(2)}:1 (minimum: {MIN_CONTRAST_RATIO}:1)</p>
                                                                    {contrastInfo.suggestedDarkMode && (
                                                                        <div className="mt-2 flex items-center gap-2">
                                                                            <span className="text-blue-700">Suggested:</span>
                                                                            <code className="px-2 py-1 bg-white rounded border border-blue-300 text-blue-900 font-mono text-xs">
                                                                                {contrastInfo.suggestedDarkMode}
                                                                            </code>
                                                                            <div 
                                                                                className="w-6 h-6 rounded border border-blue-300" 
                                                                                style={{ backgroundColor: contrastInfo.suggestedDarkMode }}
                                                                                title={contrastInfo.suggestedDarkMode}
                                                                            />
                                                                            <button
                                                                                onClick={() => {
                                                                                    const event = { target: { value: contrastInfo.suggestedDarkMode } };
                                                                                    this.handleThemeValueChange(t)(event);
                                                                                }}
                                                                                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                                            >
                                                                                Apply
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-6 space-y-4">
                                    <Button variant="outline-primary" onClick={this.addNewThemeKey}>
                                        <FontAwesomeIcon icon={["fad", "plus-circle"]} className="mr-2" />
                                        Add Theme Property
                                    </Button>
                                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                        <p className="text-sm font-medium text-blue-900 mb-2">Common theme properties:</p>
                                        <code className="text-xs text-blue-700">accent, primary-base-color, secondary-base-color</code>
                                    </div>
                                </div>
                            </div>
                            {/* Localizations Section */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                                    <FontAwesomeIcon icon={["fad", "language"]} className="mr-2 text-blue-600" />
                                    Localizations
                                    {this.state.waitingForSave && <FontAwesomeIcon icon={["fad", "spinner"]} spin={true} className="ml-2 text-blue-500"></FontAwesomeIcon>}
                                </h3>

                                {/* Language Selection */}
                                <div className="mb-6 flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-700">Add Language:</span>
                                    <Button variant="outline-secondary" size="sm" onClick={this.addLocaliztion("en")}>
                                        <FontAwesomeIcon icon={["fad", "flag-usa"]} className="mr-1" />
                                        EN
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={this.addLocaliztion("af")}>
                                        <FontAwesomeIcon icon={["fad", "flag"]} className="mr-1" />
                                        AF
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={this.addLocaliztion("ar")}>
                                        <FontAwesomeIcon icon={["fad", "flag"]} className="mr-1" />
                                        AR
                                    </Button>
                                </div>

                                {/* Language Sections */}
                                <div className="space-y-6">
                                    {this.state.localizations.length === 0 ? (
                                        <p className="text-sm text-gray-500 italic">No localizations configured. Add a language to get started.</p>
                                    ) : (
                                        this.state.localizations.map((language: any, l: number) => (
                                            <div key={`l${l}`} className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-gray-50 to-white">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 uppercase">
                                                    {language.language} -{" "}
                                                    {language.language === "en" ? "English" : language.language === "af" ? "Afrikaans" : language.language === "ar" ? "Arabic" : language.language}
                                                </h4>
                                                <div className="space-y-3">
                                                    {language.values.map((value: any, v: number) => (
                                                        <div key={`l${l}v${v}`} className="grid grid-cols-12 gap-3 items-start">
                                                            <div className="col-span-12 sm:col-span-5">
                                                                <Input
                                                                    type="text"
                                                                    placeholder="Key (e.g., welcome_message)"
                                                                    value={value.key}
                                                                    onChange={this.handleLocalizationKeyChange(l, v)}
                                                                ></Input>
                                                            </div>
                                                            <div className="col-span-12 sm:col-span-6">
                                                                <Input type="text" placeholder="Translated value" value={value.value} onChange={this.handleLocalizationValueChange(l, v)}></Input>
                                                            </div>
                                                            <div className="col-span-12 sm:col-span-1">
                                                                <Button variant="outline-danger" size="sm" onClick={this.removeLocalizationProperty(l, v)} className="w-full">
                                                                    <FontAwesomeIcon icon={["fad", "trash"]} />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-4">
                                                    <Button variant="outline-primary" size="sm" onClick={this.addNewLocalizationProperty(l)}>
                                                        <FontAwesomeIcon icon={["fad", "plus-circle"]} className="mr-2" />
                                                        Add {language.language.toUpperCase()} Property
                                                    </Button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )
        );
    }
}
